import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { TrendingUp, MapPin, Loader2 } from "lucide-react";

export default function SalaryIntel() {
  const { t } = useI18n();
  const [role, setRole] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [citiesRes, salaryRes] = await Promise.allSettled([
          api.get("/jobs/cities"),
          api.get("/intel/salary"),
        ]);
        if (cancelled) return;
        if (citiesRes.status === "fulfilled") setCities(citiesRes.value.data);
        if (salaryRes.status === "fulfilled") {
          setData(salaryRes.value.data);
        } else {
          // soft-fail: empty state, no error overlay
          setData({ sample_size: 0, min: 0, median: 0, max: 0, top_cities: [] });
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const run = async () => {
    setLoading(true);
    const params = {};
    if (role) params.role = role;
    if (city) params.city = city;
    try {
      const r = await api.get("/intel/salary", { params });
      setData(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Salary lookup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="salary-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 mb-3">/ {t("salary")}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white">
          What does Saudi pay you?
        </h1>
      </div>

      <div className="border border-white/10 bg-[#141416] p-6 grid md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">Role</span>
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="e.g. engineer"
            className="w-full bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white placeholder-white/40"
            data-testid="salary-role"
          />
        </label>
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">City</span>
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full bg-[#1a1a1c] border-b-2 border-white/15 focus:border-white outline-none py-3 text-white"
            data-testid="salary-city"
          >
            <option value="">All cities</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <button
          onClick={run}
          disabled={loading}
          className="bg-white text-black hover:bg-white/85 font-bold px-6 py-4 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 min-w-[120px]"
          data-testid="salary-run"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Analyze"}
        </button>
      </div>

      {initialLoading ? (
        <div className="border border-white/10 p-10 text-center text-white/50 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading salary data…
        </div>
      ) : data && data.sample_size > 0 ? (
        <div className="space-y-8">
          <div className="grid grid-cols-3 gap-0 border border-white/10">
            {[
              { label: "Minimum", value: data.min, accent: "text-white/70" },
              { label: "Median",  value: data.median, accent: "text-white" },
              { label: "Maximum", value: data.max, accent: "text-[#34C759]" },
            ].map((s) => (
              <div key={s.label} className="p-8 border-e border-white/10 last:border-e-0 bg-[#141416]">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-3">{s.label}</div>
                <div className={`num-ticker font-display font-black text-4xl md:text-5xl tracking-tighter ${s.accent}`}>
                  SAR {s.value.toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-white" />
              <h2 className="font-display font-bold text-xl text-white">Top hiring cities</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.top_cities.map((c) => (
                <div key={c.city} className="flex items-center justify-between p-4 border border-white/10 bg-[#141416]">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-white" />
                    <span className="font-display font-bold text-white">{c.city}</span>
                  </div>
                  <div className="num-ticker font-bold text-white">{c.count} jobs</div>
                </div>
              ))}
            </div>
          </div>

          <div className="font-mono text-xs text-white/60">
            {data.source === "live_postings" ? (
              <>Based on <span className="text-white num-ticker font-bold">{data.sample_size}</span> live postings.</>
            ) : data.source === "ksa_market_baseline" ? (
              <>
                Range derived from <span className="text-white font-bold">KSA market guides</span> (Bayt, Cooper
                Fitch, Hays). Cross-checked against{" "}
                <span className="text-white num-ticker font-bold">{data.sample_size}</span> live postings —
                most don't advertise pay in the GCC.
              </>
            ) : (
              <>Based on <span className="text-white num-ticker font-bold">{data.sample_size}</span> live postings.</>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {data && data.top_cities && data.top_cities.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-white" />
                <h2 className="font-display font-bold text-xl text-white">Top hiring cities</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.top_cities.map((c) => (
                  <div key={c.city} className="flex items-center justify-between p-4 border border-white/10 bg-[#141416]">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-white" />
                      <span className="font-display font-bold text-white">{c.city}</span>
                    </div>
                    <div className="num-ticker font-bold text-white">{c.count} jobs</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="border border-dashed border-white/15 p-10 text-center text-white/60">
            Type a role above (e.g. <span className="text-white">"nurse"</span>,{" "}
            <span className="text-white">"backend engineer"</span>,{" "}
            <span className="text-white">"sales manager"</span>) and click <span className="text-white">Analyze</span>{" "}
            to see KSA market salary ranges.
          </div>
        </div>
      )}
    </div>
  );
}
